import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { PROJECT_STATUS_CD, PROJECT_TYPE_CD } from '../constants';

/**
 * 계약정보가 선택적인 프로젝트 상태 목록
 */
const CONTRACT_INFO_OPTIONAL_STATUSES = [
  PROJECT_STATUS_CD.PROPOSAL, // 제안
  PROJECT_STATUS_CD.PROPOSAL_FAILED, // 제안 탈락
] as const;

/**
 * 프로젝트 상태가 계약정보 필수가 아닌 상태인지 확인
 */
const isContractInfoOptionalStatus = (projectStatusCd: number | string | undefined): boolean => {
  return CONTRACT_INFO_OPTIONAL_STATUSES.includes(Number(projectStatusCd));
};

const projectFormValidationSchema = Yup.object().shape({
  projectNm: Yup.string().required('프로젝트명을 입력해주세요.'),
  deprtCd: Yup.string().required('부서를 선택해주세요.'),
  projectTypeCd: Yup.string().required('프로젝트 구분을 선택해주세요.'),
  projectStatusCd: Yup.string().required('프로젝트 상태를 선택해주세요.'),

  // 계약 정보 관련 필드들을 하나의 조건으로 처리
  // 프로젝트 상태 값이 없으면 validation 미적용
  // 프로젝트 상태가 '제안' 또는 '제안 탈락'인 경우 선택적, 그 외에는 4개 필드 모두 무조건 필수
  cntrctNm: Yup.string().test({
    name: 'contractFieldsRequired',
    message: '계약명을 입력해주세요',
    test: function (value) {
      const { projectStatusCd } = this.parent;

      // 프로젝트 상태 값이 없으면 validation 통과
      if (!projectStatusCd) {
        return true;
      }

      // 제안/제안 탈락 상태: validation 통과 (선택적)
      if (isContractInfoOptionalStatus(projectStatusCd)) {
        return true;
      }

      // 그 외 상태: 무조건 필수
      return Boolean(value);
    },
  }),
  contractCmpy: Yup.object().test({
    name: 'contractFieldsRequired',
    message: '계약사를 선택해주세요.',
    test: function (value) {
      const { projectStatusCd } = this.parent;

      // 프로젝트 상태 값이 없으면 validation 통과
      if (!projectStatusCd) {
        return true;
      }

      // 제안/제안 탈락 상태: validation 통과 (선택적)
      if (isContractInfoOptionalStatus(projectStatusCd)) {
        return true;
      }

      // 그 외 상태: 무조건 필수
      return !isEmpty(value);
    },
  }),
  projectMngId: Yup.string().test({
    name: 'contractFieldsRequired',
    message: '프로젝트 관리 ID는 필수입니다.',
    test: function (value) {
      const { projectStatusCd } = this.parent;

      // 프로젝트 상태 값이 없으면 validation 통과
      if (!projectStatusCd) {
        return true;
      }

      // 제안/제안 탈락 상태: validation 통과 (선택적)
      if (isContractInfoOptionalStatus(projectStatusCd)) {
        return true;
      }

      // 그 외 상태: 무조건 필수
      return Boolean(value);
    },
  }),
  contractAmount: Yup.number()
    .nullable()
    .test({
      name: 'contractFieldsRequired',
      message: '계약금액을 입력해주세요.',
      test: function (value) {
        const { projectStatusCd } = this.parent;

        // 프로젝트 상태 값이 없으면 validation 통과
        if (!projectStatusCd) {
          return true;
        }

        // 제안/제안 탈락 상태: validation 통과 (선택적)
        if (isContractInfoOptionalStatus(projectStatusCd)) {
          return true;
        }

        // 그 외 상태: 무조건 필수 (0원 초과)
        return value != null && value > 0;
      },
    }),

  // 기성내역 validation - 과제가 아닌 경우에만 검증
  projectRoundSales: Yup.array().when('projectTypeCd', {
    is: (val: number | string | undefined) => !val || Number(val) !== PROJECT_TYPE_CD.ASSIGNMENT,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          amt: Yup.number().min(1, '기성금액을 입력해주세요.'),
        }),
      ),
    otherwise: (schema) => schema,
  }),

  // 그 외 매입내역 validation - 과제가 아닌 경우에만 검증
  purchaseOthers: Yup.array().when('projectTypeCd', {
    is: (val: number | string | undefined) => !!val && Number(val) !== PROJECT_TYPE_CD.ASSIGNMENT,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          category: Yup.string()
            .required('항목을 입력해주세요.')
            .max(100, '100자 이내로 입력해주세요.'),
          vender: Yup.string()
            .required('업체를 입력해주세요.')
            .max(100, '100자 이내로 입력해주세요.'),
        }),
      ),
    otherwise: (schema) => schema,
  }),

  // 과제 입금내역 validation - 과제인 경우에만 검증
  assignDeposits: Yup.array().when('projectTypeCd', {
    is: (val: number | string | undefined) => Number(val) === PROJECT_TYPE_CD.ASSIGNMENT,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          depositAmt: Yup.number().min(0, '입금액은 0 이상이어야 합니다.'),
        }),
      ),
    otherwise: (schema) => schema,
  }),

  // 과제용 매입내역 validation - 과제인 경우에만 검증
  purchaseAssignments: Yup.array().when('projectTypeCd', {
    is: (val: number | string | undefined) => Number(val) === PROJECT_TYPE_CD.ASSIGNMENT,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          category: Yup.string()
            .required('비목을 입력해주세요.')
            .max(100, '100자 이내로 입력해주세요.'),
          plan: Yup.string()
            .required('계획을 입력해주세요.')
            .max(100, '100자 이내로 입력해주세요.'),
          contractAmt: Yup.number().min(0, '계약금액은 0 이상이어야 합니다.'),
          usedAmt: Yup.number().min(0, '사용금액은 0 이상이어야 합니다.'),
        }),
      ),
    otherwise: (schema) => schema,
  }),
});

export default projectFormValidationSchema;
